/**
 * Login page — Google + Apple OAuth web flow buttons.
 *
 * Both kick off via a server-side redirect (`/api/v1/auth/oauth/{provider}/start`),
 * not direct nav to Apple/Google. Backend signs state, builds redirect_uri,
 * and 302s.
 *
 * In dev mode (`app_env=='development'`) a third option appears below
 * — see DevLoginForm — that bypasses OAuth entirely so you can test
 * without setting up Google/Apple credentials.
 *
 * Compliance Baseline (STA-128, STA-132) — the consent checkbox gates
 * both OAuth starts and the dev-login form. The user's ticked intent is
 * persisted to localStorage under PENDING_CONSENT_KEY; AuthCallback
 * replays it against POST /users/me/consent after the JWT is in hand.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DevLoginForm } from '../components/DevPanel';
import { useI18n } from '../i18n';

export const PENDING_CONSENT_KEY = 'pending_consent_v1';

export default function Login() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const sessionExpired = params.get('session_expired') === '1';
  const [agreed, setAgreed] = useState(false);
  const [showError, setShowError] = useState(false);

  const requireConsent = (): boolean => {
    if (!agreed) {
      setShowError(true);
      return false;
    }
    window.localStorage.setItem(PENDING_CONSENT_KEY, '1');
    return true;
  };

  const startGoogle = () => {
    if (!requireConsent()) return;
    window.location.href = `/api/v1/auth/oauth/google/start?return_to=${encodeURIComponent(window.location.origin)}`;
  };
  const startApple = () => {
    if (!requireConsent()) return;
    window.location.href = `/api/v1/auth/oauth/apple/start?return_to=${encodeURIComponent(window.location.origin)}`;
  };

  return (
    <div className="max-w-md mx-auto px-4 py-20">
      <div className="mp-eyebrow">{t('login.eyebrow')}</div>
      <h1 className="mp-display text-4xl text-tarot-gold mb-2 mt-2">{t('login.title')}</h1>
      <p className="text-tarot-muted text-sm mb-8">{t('login.sub')}</p>

      {sessionExpired && (
        <div
          role="status"
          className="mb-4 rounded border border-yellow-500/40 bg-yellow-500/10 text-yellow-300 text-xs leading-relaxed px-3 py-2"
        >
          您的登入已過期，請重新登入。
        </div>
      )}

      <label className="flex items-start gap-2 text-sm leading-relaxed text-tarot-muted cursor-pointer bg-tarot-purple/10 border border-tarot-purple/30 rounded p-3 mb-4">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => {
            setAgreed(e.target.checked);
            if (e.target.checked) setShowError(false);
          }}
          className="mt-0.5 accent-tarot-gold flex-shrink-0"
          aria-label="同意條款"
        />
        <span>
          我確認我已<strong className="text-tarot-text">滿 18 歲</strong>
          （或已取得法定代理人同意），並已詳細閱讀且同意本網站之
          <a href="/terms" target="_blank" rel="noreferrer" className="underline mx-1 text-tarot-gold">
            服務條款
          </a>
          與
          <a href="/privacy" target="_blank" rel="noreferrer" className="underline mx-1 text-tarot-gold">
            隱私權政策
          </a>
          。
        </span>
      </label>
      {showError && !agreed && (
        <p className="text-red-400 text-xs mb-4">請先勾選同意條款後再登入。</p>
      )}

      <div className="space-y-3">
        <button
          onClick={startGoogle}
          disabled={!agreed}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('login.google')}
        </button>
        <button
          onClick={startApple}
          disabled={!agreed}
          className="btn-ghost w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('login.apple')}
        </button>
      </div>

      <DevLoginForm onBeforeSubmit={requireConsent} />
    </div>
  );
}
